// /app/brands/$brandId/gallery — dedicated media gallery for a brand.
//
// Lives at its own URL (not embedded under /content/{id}) so:
//   1. It's bookmarkable — the user can pin "all generated media for
//      brand X" as a tab.
//   2. The /content/{id} page stays focused on the composer flow
//      (recent generations + posts), which is conceptually different
//      from "the visual archive."
//   3. It gets the BrandContextBar so the user can switch between
//      Brand sections, Calendar, Comments, and Gallery with one
//      click — matching the calendar/comments routes' shape.
//
// Data: walks the brand's last N generations and flattens versions.
// Same approach as the previous embedded version — gives us a uniform
// view across claude-html-* (asset URLs) and fal-* (CDN URLs) without
// requiring a new server-side endpoint.

import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Card } from "../../../components/Card";
import { Button } from "../../../components/Button";
import { useI18n } from "../../../lib/i18n";
import {
  assetURL,
  generations,
  posts as postsAPI,
  type Generation,
  type GenerationVersion,
  type Post,
} from "../../../lib/api";
import { AssetDownloadButton } from "../../../components/AssetDownloadButton";

// Cap the fan-out — page load fires N+1 detail fetches. 24 generations
// = at most ~96 image variants on a 4-variant-per-platform run. Plenty.
const GALLERY_FANOUT_LIMIT = 24;

type GalleryItem = {
  versionId: string;
  generationId: string;
  imageUrl: string;
  platform: string;
  status: GenerationVersion["status"];
  imageModel?: string | null;
  generationPrompt: string;
  createdAt: string;
};

// Filter chips for the engine-source view. "any" = all variants,
// "claude-html" = both opus + sonnet, "diffusion" = any fal-*.
type EngineFilter = "any" | "claude-html" | "diffusion";

function BrandGallery() {
  const { t } = useI18n();
  const { brandId } = Route.useParams();
  const [items, setItems] = useState<GalleryItem[] | null>(null);
  const [posts, setPosts] = useState<Post[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [engineFilter, setEngineFilter] = useState<EngineFilter>("any");
  // v0.5.20 — lightbox state. null = closed; item = open with that variant.
  const [lightbox, setLightbox] = useState<GalleryItem | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      generations.listForBrand(brandId).catch(() => ({ generations: [] as Generation[] })),
      postsAPI.listForBrand(brandId).catch(() => ({ posts: [] as Post[] })),
    ])
      .then(async ([gensRes, postsRes]) => {
        if (cancelled) return;
        setPosts(postsRes.posts);
        const recent = gensRes.generations.slice(0, GALLERY_FANOUT_LIMIT);
        const settled = await Promise.allSettled(recent.map((g) => generations.get(g.id)));
        if (cancelled) return;
        const out: GalleryItem[] = [];
        settled.forEach((r, i) => {
          if (r.status !== "fulfilled") return;
          const gen = recent[i]!;
          r.value.versions.forEach((v) => {
            if (!v.image_url) return;
            out.push({
              versionId: v.id,
              generationId: gen.id,
              imageUrl: v.image_url,
              platform: v.platform,
              status: v.status,
              imageModel: v.image_model,
              generationPrompt: gen.prompt,
              createdAt: v.created_at,
            });
          });
        });
        out.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
        setItems(out);
      })
      .catch((e) => { if (!cancelled) setLoadErr(String(e)); });
    return () => { cancelled = true; };
  }, [brandId]);

  // Promoted-to-post detection. Normalize both sides through assetURL
  // so a fal absolute URL on the version doesn't miss-match a relative
  // brand-asset URL on the post (or vice versa).
  const promotedUrls = useMemo(() => {
    const s = new Set<string>();
    (posts ?? []).forEach((p) => { if (p.image_url) s.add(assetURL(p.image_url)); });
    return s;
  }, [posts]);
  // v0.5.20 — reverse index so the lightbox can deep-link to the post
  // when this variant was promoted. Same normalization as promotedUrls.
  const postByImageUrl = useMemo(() => {
    const m = new Map<string, string>();
    (posts ?? []).forEach((p) => {
      if (p.image_url) m.set(assetURL(p.image_url), p.id);
    });
    return m;
  }, [posts]);

  // Engine-aware counts so the filter chips can show numbers.
  const counts = useMemo(() => {
    let claude = 0, diff = 0;
    (items ?? []).forEach((it) => {
      const m = (it.imageModel ?? "").toLowerCase();
      if (m.startsWith("claude-html")) claude++;
      else if (m.startsWith("fal-")) diff++;
    });
    return { any: (items ?? []).length, claude, diff };
  }, [items]);

  const filtered = useMemo(() => {
    if (!items) return null;
    if (engineFilter === "any") return items;
    if (engineFilter === "claude-html") {
      return items.filter((it) => (it.imageModel ?? "").toLowerCase().startsWith("claude-html"));
    }
    return items.filter((it) => (it.imageModel ?? "").toLowerCase().startsWith("fal-"));
  }, [items, engineFilter]);

  return (
    <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-6 sm:py-8 flex flex-col gap-5">
      {/* Header row with title + jump-back links */}
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div className="flex flex-col gap-1">
          <span className="eyebrow">Gallery</span>
          <h1 className="font-display text-2xl font-semibold">Generated media</h1>
          <p className="text-xs text-secondary max-w-prose">
            Every image variant produced for this brand, newest first.
            Includes Nexus Design compositions (uploaded as brand assets)
            and any legacy diffusion outputs.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/app/content/$brandId" params={{ brandId }}>
            <Button variant="ghost">← Content</Button>
          </Link>
          <Link to="/app/content/new" search={{ brand_id: brandId }}>
            <Button variant="primary">+ Generate</Button>
          </Link>
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex gap-2 flex-wrap" role="tablist" aria-label="Engine filter">
        <FilterChip label={`All (${counts.any})`}        active={engineFilter === "any"}         onClick={() => setEngineFilter("any")} />
        <FilterChip label={`Nexus Design (${counts.claude})`} active={engineFilter === "claude-html"} onClick={() => setEngineFilter("claude-html")} />
        <FilterChip label={`Diffusion (${counts.diff})`} active={engineFilter === "diffusion"}   onClick={() => setEngineFilter("diffusion")} />
      </div>

      {/* Grid */}
      {loadErr ? (
        <Card><p className="text-error text-sm">{loadErr}</p></Card>
      ) : filtered == null ? (
        <p className="text-secondary text-sm">{t("common.loading")}</p>
      ) : filtered.length === 0 ? (
        <Card className="!p-8">
          <div className="text-center flex flex-col gap-3 items-center">
            <span className="text-4xl opacity-50" aria-hidden>🖼️</span>
            <p className="text-secondary text-sm max-w-md">
              {engineFilter === "any"
                ? "No images yet. Every generation you create will appear here as a thumbnail."
                : engineFilter === "claude-html"
                  ? "No Nexus Design compositions yet. Generate your first to see them here."
                  : "No legacy diffusion images. Recent generations all use the Nexus Design pipeline."}
            </p>
            <Link to="/app/content/new" search={{ brand_id: brandId }}>
              <Button variant="primary">+ Generate your first</Button>
            </Link>
          </div>
        </Card>
      ) : (
        <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
          {filtered.map((item) => {
            const promoted = promotedUrls.has(assetURL(item.imageUrl));
            const engineLabel = engineShortLabel(item.imageModel);
            return (
              <li key={item.versionId} className="group relative">
                {/* v0.5.20 — clicking the thumb opens a lightbox modal
                    instead of deep-navigating to the generation detail.
                    Lets the operator preview at full size + download
                    PNG/JPEG without leaving the gallery. Direct
                    navigation to gen/post detail still available via
                    the lightbox's footer buttons. */}
                <button
                  type="button"
                  onClick={() => setLightbox(item)}
                  className="block w-full ring-nexus rounded-[10px]"
                >
                  <div className="aspect-square rounded-[10px] overflow-hidden border border-border-subtle bg-overlay/30">
                    <img
                      src={assetURL(item.imageUrl)}
                      alt={item.generationPrompt}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    />
                  </div>
                </button>
                {promoted ? (
                  <span
                    className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-success/90 text-white px-2 py-0.5 text-[10px] font-medium backdrop-blur-sm pointer-events-none"
                    title="Promoted to a published post"
                  >
                    ✓ Promoted
                  </span>
                ) : null}
                <span
                  className={[
                    "absolute top-2 right-2 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-mono backdrop-blur-sm pointer-events-none",
                    engineLabel.tone === "claude" ? "bg-accent/80 text-white"
                      : engineLabel.tone === "diffusion" ? "bg-info/70 text-white"
                      : "bg-bg/70 text-secondary",
                  ].join(" ")}
                  title={item.imageModel ?? "unknown engine"}
                >
                  {engineLabel.short}
                </span>
                <AssetDownloadButton
                  imageUrl={item.imageUrl}
                  hideUntilHover
                  triggerClassName="absolute bottom-1.5 right-1.5"
                />
                <div className="mt-1.5 flex items-baseline justify-between gap-1">
                  <p className="text-[11px] text-muted truncate font-mono" title={item.generationPrompt}>
                    {item.platform}
                  </p>
                  <p className="text-[11px] text-muted font-mono">
                    {new Date(item.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {/* v0.5.20 — lightbox modal. Renders only when an item is open. */}
      {lightbox ? (
        <GalleryLightbox
          item={lightbox}
          postId={postByImageUrl.get(assetURL(lightbox.imageUrl))}
          onClose={() => setLightbox(null)}
        />
      ) : null}
    </div>
  );
}

// GalleryLightbox — full-size preview with PNG/JPEG download +
// links to the source generation and (when promoted) the published post.
// Rendered as a fixed modal with backdrop; closes on backdrop click + Esc.
function GalleryLightbox({
  item,
  postId,
  onClose,
}: {
  item: GalleryItem;
  postId?: string;
  onClose: () => void;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 sm:p-8"
      role="dialog"
      aria-modal="true"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative max-w-5xl w-full max-h-[90vh] flex flex-col gap-3">
        {/* Image — natural aspect, never bigger than viewport */}
        <div className="flex-1 min-h-0 flex items-center justify-center">
          <img
            src={assetURL(item.imageUrl)}
            alt={item.generationPrompt}
            className="max-w-full max-h-full object-contain rounded-[12px] shadow-2xl"
          />
        </div>

        {/* Action bar */}
        <div className="flex items-center justify-between gap-3 flex-wrap bg-shell/90 backdrop-blur-md rounded-[12px] border border-border-default p-3">
          <div className="flex flex-col min-w-0">
            <span className="text-xs font-mono text-muted capitalize">{item.platform} · {new Date(item.createdAt).toLocaleDateString()}</span>
            <span className="text-sm text-primary truncate max-w-[60ch]" title={item.generationPrompt}>
              {item.generationPrompt}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <AssetDownloadButton imageUrl={item.imageUrl} label="Download" />
            {postId ? (
              <Link
                to="/app/posts/$postId"
                params={{ postId }}
                className="ring-nexus rounded-md px-3 py-2 text-sm border border-border-default text-secondary hover:text-primary hover:bg-elevated transition"
                onClick={onClose}
              >
                View post →
              </Link>
            ) : null}
            <Link
              to="/app/generations/$generationId"
              params={{ generationId: item.generationId }}
              className="ring-nexus rounded-md px-3 py-2 text-sm border border-border-default text-secondary hover:text-primary hover:bg-elevated transition"
              onClick={onClose}
            >
              View generation →
            </Link>
            <button
              type="button"
              onClick={onClose}
              className="ring-nexus rounded-md px-3 py-2 text-sm border border-border-default text-secondary hover:text-primary hover:bg-elevated transition"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FilterChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={[
        "h-8 px-3 rounded-full text-[12px] font-medium transition-colors ring-nexus",
        active
          ? "bg-red-muted text-primary border border-red/40"
          : "bg-overlay/40 text-secondary hover:text-primary border border-border-subtle",
      ].join(" ")}
    >
      {label}
    </button>
  );
}

// engineShortLabel — collapses the image_model field into a 2-character
// pill ("HTM" for Claude-HTML, "FAL" for diffusion) so the grid stays
// dense while still surfacing which engine made what.
function engineShortLabel(model: string | null | undefined): { short: string; tone: "claude" | "diffusion" | "neutral" } {
  const m = (model ?? "").toLowerCase();
  if (m.startsWith("claude-html")) return { short: "HTM", tone: "claude" };
  if (m.startsWith("fal-")) return { short: "FAL", tone: "diffusion" };
  return { short: "·", tone: "neutral" };
}

export const Route = createFileRoute("/app/brands/$brandId_/gallery")({
  component: BrandGallery,
});
